import { IlmApiKey, SecurityApi } from '@portal/pkg-api/gen/security'
import { IMercatusAxiosRequestConfig } from '@migrate/utils/axiosInterceptor'
import { RestApiConfiguration } from '@portal/pkg-api/config'
import LoadableStore, { LoadingState } from '@migrate/stores/LoadableStore'
import { action, makeObservable, override, runInAction } from 'mobx'

/**
 * Contains security related data.
 */
class SecurityStore extends LoadableStore {
  public static STORE_NAME = 'securityStore'

  private static REQUESTED_WITH_HEADER = 'X-Requested-With'
  private static REQUESTED_WITH_VALUE = 'XMLHttpRequest'
  private static API_KEY: IlmApiKey = { key: 'ui.2' }
  private static CSRF_REQUIRED_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

  private csrfTokenHeaderName: string
  private csrfToken: string
  private api: SecurityApi

  constructor() {
    super()
    this.csrfTokenHeaderName = 'X-Mercatus-Token'
    this.csrfToken = ''
    this.api = new SecurityApi(RestApiConfiguration)
    makeObservable(this)
  }

  @action
  public async load() {
    this.state = LoadingState.LOADING
    const response = await this.api.init2(SecurityStore.API_KEY, { sendCsrfToken: false })
    this.csrfTokenHeaderName = response.data.headerName
    this.csrfToken = response.data.token
    runInAction(() => {
      this.state = LoadingState.LOADED
    })
  }

  @override
  public async unload() {
    this.csrfToken = ''
  }

  /**
   * Add security related headers to an Axios request.
   */
  public addRequestHeadersIfRequired(
    request: IMercatusAxiosRequestConfig,
  ): IMercatusAxiosRequestConfig {
    if (request.headers && this.needsCsrfHeader(request)) {
      return this.addRequestHeaders(request)
    }
    return request
  }

  public addRequestHeaders(request: IMercatusAxiosRequestConfig): IMercatusAxiosRequestConfig {
    request.headers.set(SecurityStore.REQUESTED_WITH_HEADER, SecurityStore.REQUESTED_WITH_VALUE)
    request.headers.set(this.csrfTokenHeaderName, this.csrfToken)
    return request
  }

  private needsCsrfHeader(request: IMercatusAxiosRequestConfig): boolean {
    return (
      !!request.sendCsrfToken &&
      !!request.method &&
      SecurityStore.CSRF_REQUIRED_METHODS.indexOf(request.method.toUpperCase()) >= 0 &&
      !!this.csrfToken
    )
  }
}

const instance = new SecurityStore()
export default instance
